'use no memo';

import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EditTableIcon from '../../../assets/icons/edit-table.svg?react';
import { ManageColumnsDialog } from '../../dialogs/ManageColumnsDialog';
import { ErrorDisplay } from '../../ErrorDisplay';
import { IconButton } from '../../IconButton';
import { SkeletonRow } from '../../tables/renderers/SkeletonRow';
import { SortableTableHead } from '../../tables/SortableTableHead';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  TableWrapper,
} from '../../tables/Table';
import type { DataTableProps } from './types';
import {
  DEFAULT_MIN_COLUMN_SIZE,
  getColumnFlexStyle,
} from '../../tables/columnStyle';
import { cn } from '../../../utils/className';
import { logger } from '../../../utils/logger';

/**
 * DataTable - Reusable desktop data table component
 *
 * Features:
 * - Fixed-width columns for pinned/non-resizable columns
 * - Flexible columns that share remaining space proportionally
 * - Column pinning (left/right)
 * - Sorting
 * - Pagination
 * - Loading states
 * - Empty states
 */
export function DataTable<TData>({
  data,
  columns,
  isLoading = false,
  error,
  getRowId,
  pageIndex: propPageIndex,
  pageSize: propPageSize,
  totalPages,
  totalCount,
  onPageChange: propOnPageChange,
  onPageSizeChange,
  rowsPerPageOptions = [5, 10, 20, 50, 100],
  sorting: propSorting,
  onSortingChange: propOnSortingChange,
  columnPinning,
  onColumnPinningChange,
  columnVisibility,
  onColumnVisibilityChange,
  defaultColumnVisibility,
  columnOrder,
  onColumnOrderChange,
  getSubRows,
  expanded,
  onExpandedChange,
  columnSizing: propColumnSizing,
  onColumnSizingChange,
  resetColumnSizing,
  columnFilters,
  toolbarActions,
  activeFilters,
  enableManageColumns = false,
  onRowClick,
  selectedIds,
  emptyStateMessage = 'No data found',
  emptyStateDescription = 'Try adjusting your filters or search criteria',
  title,
  ariaLabel = 'Data table',
}: DataTableProps<TData>) {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);

  // Whether content is scrolled underneath the pinned columns on either side
  const [pinnedEdges, setPinnedEdges] = useState({
    left: false,
    right: false,
  });

  // Use props for pagination and sorting (URL params are handled in hooks now)
  const pageIndex = propPageIndex ?? 0;
  const pageSize = propPageSize ?? 10;
  const onPageChange = propOnPageChange ?? (() => {});
  const sorting = propSorting ?? [];
  const onSortingChange = propOnSortingChange;

  // Handle page size change
  const handlePageSizeChange = onPageSizeChange;

  // Filter out any null/undefined items from data to prevent sorting errors
  const safeData = useMemo(
    () => (data ?? []).filter((item): item is TData => item != null),
    [data]
  );

  // Get valid column IDs to filter stale columnSizing entries from localStorage
  const validColumnIds = useMemo(() => {
    const ids = new Set<string>();
    columns.forEach((col) => {
      const colId =
        'id' in col && col.id
          ? col.id
          : 'accessorKey' in col
            ? String(col.accessorKey)
            : undefined;
      if (colId) ids.add(colId);
    });
    return ids;
  }, [columns]);

  // Filter columnSizing to only include valid column IDs
  const safeColumnSizing = useMemo(() => {
    if (!propColumnSizing) return {};
    const filtered: typeof propColumnSizing = {};
    for (const [key, value] of Object.entries(propColumnSizing)) {
      if (validColumnIds.has(key)) {
        filtered[key] = value;
      }
    }
    return filtered;
  }, [propColumnSizing, validColumnIds]);

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: safeData,
    columns,
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows,
    onExpandedChange,
    manualSorting: true, // Data is sorted server-side
    onColumnPinningChange,
    onColumnVisibilityChange,
    onColumnOrderChange,
    onColumnSizingChange,
    manualPagination: true,
    pageCount: totalPages,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    // Set default minimum column size to prevent columns from being too narrow
    defaultColumn: {
      minSize: 180, // Minimum width to fit header content
    },
    getRowId,
    state: {
      sorting,
      columnPinning,
      columnVisibility,
      columnOrder,
      columnSizing: safeColumnSizing,
      // Never undefined: that would shadow TanStack's own initial state and
      // crash its expand-all getters on tables without expansion
      expanded: expanded ?? {},
      pagination: {
        pageIndex,
        pageSize,
      },
    },
  });

  // Proportional sizing runs on mount and re-runs while the sizing is still
  // the automatic one (i.e. the user has not resized a column by hand)
  const hasInitializedRef = useRef(false);
  const lastAutoSizingRef = useRef<Record<string, number> | null>(null);
  const lastFitWidthRef = useRef(0);

  /**
   * Distribute the container width over the visible resizable columns,
   * proportionally to their default sizes. Columns whose share would fall
   * below their minimum are fixed at that minimum and the rest rescaled, so
   * the assigned widths always sum to exactly the container width.
   */
  const applyProportionalSizing = useCallback(
    (measuredWidth: number) => {
      if (!onColumnSizingChange) return;

      // Whole pixels only. At a browser zoom other than 100% the observer
      // reports fractional widths while clientWidth is an integer; feeding
      // both into the fit made every fit differ from the last one and the
      // table re-rendered without end
      const containerWidth = Math.floor(measuredWidth);

      // Pinned columns (sticky at the edges) and non-resizable center columns
      // keep their declared size; everything else shares the remaining width
      let fixedWidth = 0;
      let pool: Array<{ id: string; defaultSize: number; minSize: number }> =
        [];

      columns.forEach((col) => {
        const colId =
          'id' in col && col.id
            ? col.id
            : 'accessorKey' in col
              ? String(col.accessorKey)
              : 'unknown';

        const isVisible = columnVisibility?.[colId] !== false;
        if (!isVisible) return;

        const defaultSize = col.size ?? 150;
        const isPinned =
          columnPinning?.left?.includes(colId) ||
          columnPinning?.right?.includes(colId);
        const isResizable = col.enableResizing !== false;

        if (isPinned || !isResizable) {
          fixedWidth += defaultSize;
        } else {
          pool.push({
            id: colId,
            defaultSize,
            minSize:
              col.minSize ?? Math.min(defaultSize, DEFAULT_MIN_COLUMN_SIZE),
          });
        }
      });

      let availableWidth = containerWidth - fixedWidth;
      if (availableWidth <= 0 || pool.length === 0) return;

      const newColumnSizing: Record<string, number> = {};
      while (pool.length > 0) {
        const poolSize = pool.reduce((sum, col) => sum + col.defaultSize, 0);
        const scaleFactor = availableWidth / poolSize;
        const clamped = pool.filter(
          (col) => col.defaultSize * scaleFactor < col.minSize
        );

        if (clamped.length === 0) {
          let allocatedWidth = 0;
          pool.forEach(({ id, defaultSize }) => {
            const roundedWidth = Math.floor(defaultSize * scaleFactor);
            newColumnSizing[id] = roundedWidth;
            allocatedWidth += roundedWidth;
          });
          // Give the rounding leftovers to the last column so the widths sum
          // to the container width exactly
          const remainingPixels = availableWidth - allocatedWidth;
          if (remainingPixels > 0) {
            newColumnSizing[pool[pool.length - 1].id] += remainingPixels;
          }
          break;
        }

        clamped.forEach((col) => {
          newColumnSizing[col.id] = col.minSize;
          availableWidth -= col.minSize;
        });
        pool = pool.filter((col) => !clamped.includes(col));
        if (availableWidth <= 0) {
          pool.forEach((col) => {
            newColumnSizing[col.id] = col.minSize;
          });
          break;
        }
      }

      lastFitWidthRef.current = containerWidth;
      const previous = lastAutoSizingRef.current;
      const unchanged =
        previous !== null &&
        Object.keys(previous).length === Object.keys(newColumnSizing).length &&
        Object.keys(newColumnSizing).every(
          (id) => previous[id] === newColumnSizing[id]
        );
      if (unchanged) return;

      lastAutoSizingRef.current = newColumnSizing;
      onColumnSizingChange(newColumnSizing);
    },
    [columns, columnVisibility, columnPinning, onColumnSizingChange]
  );

  /**
   * Reset column sizing, then fill the available width proportionally.
   */
  const handleResetWithProportionalFill = useCallback(() => {
    resetColumnSizing?.();
    const containerWidth = tableContainerRef.current?.clientWidth;
    if (containerWidth && containerWidth > 0) {
      applyProportionalSizing(containerWidth);
    }
  }, [resetColumnSizing, applyProportionalSizing]);

  useEffect(() => {
    if (!onColumnSizingChange) return;

    const container = tableContainerRef.current;
    if (!container) return;

    // The sizing is "automatic" until the user drags a column resize handle:
    // either nothing is set yet, or it is exactly what the last fit applied
    const sizingIsAuto = () => {
      if (!propColumnSizing || Object.keys(propColumnSizing).length === 0) {
        return true;
      }
      const autoSizing = lastAutoSizingRef.current;
      if (!autoSizing) return false;
      const keys = Object.keys(propColumnSizing);
      return (
        keys.length === Object.keys(autoSizing).length &&
        keys.every((key) => propColumnSizing[key] === autoSizing[key])
      );
    };

    const fitIfNeeded = (containerWidth: number) => {
      if (containerWidth <= 0) return;
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        if (sizingIsAuto()) {
          applyProportionalSizing(containerWidth);
        }
        return;
      }
      if (containerWidth !== lastFitWidthRef.current && sizingIsAuto()) {
        applyProportionalSizing(containerWidth);
      }
    };

    // Keep observing after the initial fit: when the container width changes
    // (window resize, a scrollbar appearing once data loads) and the user has
    // not resized any column manually, re-fit the columns to the new width
    // Measure through clientWidth here as well, never through the entry's
    // fractional contentRect, so both fit paths compare the same integer
    const resizeObserver = new ResizeObserver(() => {
      fitIfNeeded(container.clientWidth);
    });
    resizeObserver.observe(container);
    fitIfNeeded(container.clientWidth);

    return () => resizeObserver.disconnect();
  }, [propColumnSizing, onColumnSizingChange, applyProportionalSizing]);

  // Track whether content is scrolled beneath the pinned columns, so their
  // edges can show a divider only while something actually slides under them
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const updatePinnedEdges = () => {
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      const left = container.scrollLeft > 0;
      const right = container.scrollLeft < maxScrollLeft - 1;
      setPinnedEdges((prev) =>
        prev.left === left && prev.right === right ? prev : { left, right }
      );
    };

    updatePinnedEdges();
    container.addEventListener('scroll', updatePinnedEdges, { passive: true });
    const resizeObserver = new ResizeObserver(updatePinnedEdges);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', updatePinnedEdges);
      resizeObserver.disconnect();
    };
  }, [safeColumnSizing, columnVisibility]);

  // Track previous column visibility to detect changes
  const prevColumnVisibilityRef =
    useRef<typeof columnVisibility>(columnVisibility);

  // Re-apply proportional sizing when column visibility changes
  useEffect(() => {
    // Skip on initial mount (handled by the initialization effect above)
    if (!hasInitializedRef.current) return;

    // Check for columns that are visible (not explicitly set to false in visibility object)
    const getVisibleColumnIds = (visibility: typeof columnVisibility) => {
      const visibleIds: string[] = [];
      columns.forEach((col) => {
        const colId =
          'id' in col && col.id
            ? col.id
            : 'accessorKey' in col
              ? String(col.accessorKey)
              : 'unknown';
        // Column is visible if not explicitly set to false
        if (visibility?.[colId] !== false) {
          visibleIds.push(colId);
        }
      });
      return visibleIds.sort().join(',');
    };

    const prevVisible = getVisibleColumnIds(prevColumnVisibilityRef.current);
    const currentVisible = getVisibleColumnIds(columnVisibility);

    // Update ref for next comparison
    prevColumnVisibilityRef.current = columnVisibility;

    // If visibility changed, re-apply proportional sizing
    if (prevVisible !== currentVisible) {
      const containerWidth = tableContainerRef.current?.clientWidth;
      if (containerWidth && containerWidth > 0) {
        applyProportionalSizing(containerWidth);
      }
    }
  }, [columnVisibility, columns, applyProportionalSizing]);

  // One style function for header, data and skeleton cells (see columnStyle.ts)
  const getColumnStyle = getColumnFlexStyle<TData>;

  /**
   * Fit a column to its widest rendered content (double-click on the resize
   * handle), so no cell in the column shows an ellipsis any more. Each cell
   * is measured by cloning it at natural width without its truncation
   * constraints; the header cell participates through its own data-column-id.
   */
  const autoFitColumn = useCallback(
    (columnId: string) => {
      const container = tableContainerRef.current;
      if (!container || !onColumnSizingChange) return;

      let contentWidth = 0;
      container
        .querySelectorAll<HTMLElement>(
          `[data-column-id="${CSS.escape(columnId)}"]`
        )
        .forEach((cellElement) => {
          const clone = cellElement.cloneNode(true) as HTMLElement;
          clone.style.position = 'absolute';
          clone.style.visibility = 'hidden';
          clone.style.width = 'max-content';
          clone.style.minWidth = '0';
          clone.style.maxWidth = 'none';
          clone.querySelectorAll<HTMLElement>('*').forEach((el) => {
            el.classList.remove('truncate');
            el.style.maxWidth = 'none';
            el.style.minWidth = '0';
          });
          container.appendChild(clone);
          contentWidth = Math.max(
            contentWidth,
            Math.ceil(clone.getBoundingClientRect().width)
          );
          clone.remove();
        });

      if (contentWidth === 0) return;

      // Never fit below the column's effective minimum (same rule as render)
      const columnDef = table.getColumn(columnId)?.columnDef;
      const declaredSize = columnDef?.size ?? 150;
      const minSize =
        columnDef?.minSize ?? Math.min(declaredSize, DEFAULT_MIN_COLUMN_SIZE);

      onColumnSizingChange((prev) => ({
        ...prev,
        [columnId]: Math.max(contentWidth, minSize),
      }));
    },
    [table, onColumnSizingChange]
  );

  // Crisp hairline on a pinned column's edge while content slides beneath
  // it; empty (no divider) when the table is not scrolled that way. A blurred
  // shadow would band at every row boundary because each cell paints its own.
  const pinnedLeftEdgeClass = pinnedEdges.left
    ? '[box-shadow:inset_-1px_0_0_0_var(--color-border-secondary)]'
    : '';
  const pinnedRightEdgeClass = pinnedEdges.right
    ? '[box-shadow:inset_1px_0_0_0_var(--color-border-secondary)]'
    : '';

  return (
    <>
      {/* Manage Columns Dialog */}
      {enableManageColumns && (
        <ManageColumnsDialog
          open={columnsDialogOpen}
          onOpenChange={setColumnsDialogOpen}
          columns={columns}
          columnVisibility={columnVisibility}
          columnOrder={columnOrder}
          defaultColumnVisibility={defaultColumnVisibility}
          onResetColumnSizing={handleResetWithProportionalFill}
          onColumnsChange={(visibleColumnIds, newColumnOrder) => {
            // Convert visible column IDs to visibility state
            const visibilityState: Record<string, boolean> = {};
            columns.forEach((col) => {
              // Use same ID extraction logic as ManageColumnsDialog: prefer id over accessorKey
              const colId =
                'id' in col && col.id
                  ? col.id
                  : 'accessorKey' in col
                    ? String(col.accessorKey)
                    : 'unknown';
              // Keep pinned columns (select, actions) always visible
              if (
                col.enableHiding === false ||
                colId === 'select' ||
                colId === 'actions'
              ) {
                visibilityState[colId] = true;
              } else {
                // Hidden columns are explicitly set to false
                visibilityState[colId] = visibleColumnIds.includes(colId);
              }
            });

            logger.debug('[DataTable] Saving column changes:', {
              visibilityState,
              newColumnOrder,
            });

            // Call the callbacks directly with values (not updater functions)
            onColumnVisibilityChange?.(visibilityState);
            onColumnOrderChange?.(newColumnOrder);
          }}
        />
      )}

      <div
        className='flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden'
      >
        {/* Title and Actions - stays fixed, never scrolls */}
        {(title || toolbarActions) && (
          <div
            className='flex h-18 shrink-0 items-center justify-between gap-4
              bg-white py-5 pr-6 pl-6'
          >
            {title && <h1 className='size-h3 text-focus font-bold'>{title}</h1>}
            {toolbarActions && (
              <div className='flex items-center gap-2'>{toolbarActions}</div>
            )}
          </div>
        )}

        {/* Active Filter Tags - below title, above table */}
        {activeFilters && (
          <div className='-mt-2 shrink-0 bg-white px-6 pb-4'>
            {activeFilters}
          </div>
        )}

        <TableWrapper
          scrollContainerRef={tableContainerRef}
          footer={
            <TablePagination
              currentPage={pageIndex + 1}
              totalPages={totalPages}
              rowsPerPage={pageSize}
              totalItems={totalCount || 0}
              onPageChange={(page) => onPageChange(page - 1)}
              onRowsPerPageChange={handlePageSizeChange}
              onGoToPage={(page) => onPageChange(page - 1)}
              rowsPerPageOptions={rowsPerPageOptions}
              isLoading={isLoading}
            />
          }
        >
          <Table
            style={{
              width: '100%',
            }}
            aria-label={ariaLabel}
          >
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className='flex w-full'>
                  {/* Left pinned columns */}
                  {headerGroup.headers
                    .filter((header) => header.column.getIsPinned() === 'left')
                    .map((header) => {
                      const canSort = header.column.getCanSort();
                      const filterElement = columnFilters?.(header.column);

                      const edgeClass = header.column.getIsLastColumn('left')
                        ? pinnedLeftEdgeClass
                        : '';

                      return canSort ? (
                        <SortableTableHead
                          key={header.id}
                          column={header.column}
                          header={header}
                          filterElement={filterElement}
                          className={edgeClass}
                          style={getColumnStyle(header.column, {
                            position: 'sticky',
                            left: header.column.getStart('left'),
                            zIndex: 1,
                          })}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </SortableTableHead>
                      ) : (
                        <TableHead
                          key={header.id}
                          className={edgeClass}
                          style={getColumnStyle(header.column, {
                            position: 'sticky',
                            left: header.column.getStart('left'),
                            zIndex: 1,
                          })}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}

                  {/* Center (non-pinned) columns */}
                  {(() => {
                    const centerHeaders = headerGroup.headers.filter(
                      (header) => !header.column.getIsPinned()
                    );
                    return centerHeaders.map((header) => {
                      const canSort = header.column.getCanSort();
                      const filterElement = columnFilters?.(header.column);
                      // Allow resizing for non-fixed columns
                      const canResizeColumn =
                        header.column.columnDef.enableResizing !== false;

                      // Use SortableTableHead for all columns to get consistent
                      // resize handle behavior
                      return (
                        <SortableTableHead
                          key={header.id}
                          column={header.column}
                          header={header}
                          filterElement={canSort ? filterElement : undefined}
                          className='relative'
                          style={getColumnStyle(header.column)}
                          canResize={canResizeColumn}
                          onAutoFit={() => autoFitColumn(header.column.id)}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </SortableTableHead>
                      );
                    });
                  })()}

                  {/* Right pinned columns */}
                  {headerGroup.headers
                    .filter((header) => header.column.getIsPinned() === 'right')
                    .map((header) => {
                      const canSort = header.column.getCanSort();
                      const filterElement = columnFilters?.(header.column);

                      const edgeClass = header.column.getIsFirstColumn('right')
                        ? pinnedRightEdgeClass
                        : '';

                      return canSort ? (
                        <SortableTableHead
                          key={header.id}
                          column={header.column}
                          header={header}
                          filterElement={filterElement}
                          className={edgeClass}
                          style={getColumnStyle(header.column, {
                            position: 'sticky',
                            right: header.column.getAfter('right'),
                            zIndex: 1,
                          })}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </SortableTableHead>
                      ) : (
                        <TableHead
                          key={header.id}
                          className={cn(
                            header.id === 'actions' ? 'justify-center' : '',
                            edgeClass
                          )}
                          style={getColumnStyle(header.column, {
                            position: 'sticky',
                            right: header.column.getAfter('right'),
                            zIndex: 1,
                          })}
                        >
                          {header.isPlaceholder ? null : header.id ===
                              'actions' && enableManageColumns ? (
                            <IconButton
                              aria-label='Manage columns'
                              icon={<EditTableIcon width={16} height={16} />}
                              onClick={() => setColumnsDialogOpen(true)}
                              className='text-secondary hover:text-primary h-4
                                w-4'
                            />
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}
                        </TableHead>
                      );
                    })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                // Show skeleton rows during loading
                Array.from({ length: pageSize }).map((_, index) => (
                  <SkeletonRow
                    key={`skeleton-${index}`}
                    columns={table.getVisibleLeafColumns()}
                  />
                ))
              ) : error ? (
                // Show error state
                <TableRow>
                  <TableCell
                    colSpan={table.getAllColumns().length}
                    className='h-40'
                  >
                    <div
                      className='flex flex-col items-center justify-center
                        gap-2'
                    >
                      <ErrorDisplay error={error} />
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length === 0 ? (
                // Show empty state when no data
                <TableRow className='flex w-full'>
                  <TableCell className='h-40 w-full justify-center text-center'>
                    <div
                      className='text-secondary flex flex-col items-center
                        justify-center gap-2'
                    >
                      <p className='size-p1 font-medium'>{emptyStateMessage}</p>
                      <p className='size-p2'>{emptyStateDescription}</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => {
                  // Get highlighting status from row meta if available
                  const isHighlighted = (
                    row.original as unknown as { __highlighted?: boolean }
                  ).__highlighted;
                  // A nested row sits on a tinted ground, so it reads as part
                  // of the row above it
                  const surfaceClass =
                    row.depth > 0 ? 'bg-surface-primary-support' : 'bg-white';
                  return (
                    <TableRow
                      key={row.id}
                      data-state={
                        row.getIsSelected() || selectedIds?.includes(row.id)
                          ? 'selected'
                          : undefined
                      }
                      onClick={() => onRowClick?.(row.original)}
                      className={`group flex w-full cursor-pointer ${
                        isHighlighted ? 'highlight-flash' : ''
                      }`}
                    >
                      {/* Left pinned cells */}
                      {row
                        .getVisibleCells()
                        .filter((cell) => cell.column.getIsPinned() === 'left')
                        .map((cell) => (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              `group-hover:bg-surface-focus-support
                                group-data-[state=selected]:bg-surface-focus-support
                                h-12 overflow-hidden`,
                              surfaceClass,
                              cell.column.getIsLastColumn('left')
                                ? pinnedLeftEdgeClass
                                : ''
                            )}
                            style={getColumnStyle(cell.column, {
                              position: 'sticky',
                              left: cell.column.getStart('left'),
                              zIndex: 1,
                            })}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}

                      {/* Center (non-pinned) cells */}
                      {(() => {
                        const centerCells = row
                          .getVisibleCells()
                          .filter((cell) => !cell.column.getIsPinned());
                        return centerCells.map((cell) => {
                          return (
                            <TableCell
                              key={cell.id}
                              data-column-id={cell.column.id}
                              className={cn(
                                `group-hover:bg-surface-focus-support group-data-[state=selected]:bg-surface-focus-support h-12 overflow-hidden`,
                                surfaceClass
                              )}
                              style={getColumnStyle(cell.column)}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          );
                        });
                      })()}

                      {/* Right pinned cells */}
                      {row
                        .getVisibleCells()
                        .filter((cell) => cell.column.getIsPinned() === 'right')
                        .map((cell) => (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              `group-hover:bg-surface-focus-support
                                group-data-[state=selected]:bg-surface-focus-support
                                h-12 overflow-hidden`,
                              surfaceClass,
                              cell.column.getIsFirstColumn('right')
                                ? pinnedRightEdgeClass
                                : ''
                            )}
                            style={getColumnStyle(cell.column, {
                              position: 'sticky',
                              right: cell.column.getAfter('right'),
                              zIndex: 1,
                            })}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableWrapper>
      </div>
    </>
  );
}
